import { RESULT_ITEM_TYPES, uniqueIds } from 'features/result-content/resultContent';
import { localizedName } from 'shared/utilities/format';

export const EXPECTED_TOP_THREE_PROFILE_COUNT = 120;
export const SCORABLE_PATTERN_COUNT = 6;

export function isValidVersionId(value) {
  if (value == null) {
    return false;
  }
  const text = String(value).trim();
  if (text === '' || text === '0' || text === 'undefined' || text === 'null') {
    return false;
  }
  const numeric = Number(text);
  if (Number.isFinite(numeric) && numeric <= 0) {
    return false;
  }
  return true;
}

export function resolveVersionPrimaryKey(version) {
  if (!version || !isValidVersionId(version.id)) {
    return '';
  }
  return String(version.id);
}

export function versionPatterns(version) {
  const list = version?.patterns || version?.scorable_patterns || version?.professional_patterns || [];
  return Array.isArray(list) ? list : [];
}

export function hasExactlySixScorablePatterns(version) {
  return versionPatterns(version).length === SCORABLE_PATTERN_COUNT;
}

export function normalizeRepairResult(data = {}) {
  const payload = data && typeof data === 'object' ? data : {};
  const nested = payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
    ? payload.data
    : payload;
  const created = Number(nested.created ?? nested.created_count ?? nested.profiles_created ?? 0);
  const existing = Number(nested.existing ?? nested.existing_count ?? nested.profiles_existing ?? 0);
  const conflicts = Number(nested.conflicts ?? nested.conflict_count ?? nested.profiles_conflicted ?? 0);
  const total = Number(
    nested.total
    ?? nested.total_count
    ?? nested.profiles_total
    ?? nested.actual_profiles
    ?? nested.found_profiles
    ?? (Number.isFinite(created) && Number.isFinite(existing) ? created + existing : 0),
  );
  return {
    created: Number.isFinite(created) ? created : 0,
    existing: Number.isFinite(existing) ? existing : 0,
    conflicts: Number.isFinite(conflicts) ? conflicts : 0,
    total: Number.isFinite(total) ? total : 0,
  };
}

export function logTopThreeDiagnostics({ method, url, versionId, status, code } = {}) {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  console.info('[top-three-profiles]', {
    method,
    url,
    versionId: isValidVersionId(versionId) ? String(versionId) : null,
    status: status ?? null,
    code: code || null,
  });
}

export const RELATION_TYPES = [
  'key_strength',
  'professional_growth_challenge_opportunity',
  'suggested_career_path',
  'skill_worth_developing',
];

export const MISSING_CONTENT_FILTERS = [
  { value: 'personal_summary', labelKey: 'profiles.missing.personalSummary' },
  { value: 'key_strength', labelKey: 'profiles.missing.strength' },
  { value: 'professional_growth_challenge_opportunity', labelKey: 'profiles.missing.growth' },
  { value: 'suggested_career_path', labelKey: 'profiles.missing.careerPath' },
  { value: 'skill_worth_developing', labelKey: 'profiles.missing.skill' },
];

const TYPE_ALIASES = {
  growth_opportunity: 'professional_growth_challenge_opportunity',
  growth: 'professional_growth_challenge_opportunity',
  career_path: 'suggested_career_path',
  skill: 'skill_worth_developing',
  skills: 'skill_worth_developing',
};

const RELATION_KEYS = {
  key_strength: ['key_strengths', 'ordered_key_strengths', 'key_strength_items'],
  professional_growth_challenge_opportunity: [
    'growth_opportunities',
    'professional_growth_challenge_opportunities',
    'ordered_growth_opportunities',
    'growth_items',
  ],
  suggested_career_path: ['career_paths', 'suggested_career_paths', 'ordered_career_paths', 'career_path_items'],
  skill_worth_developing: ['skills', 'skills_worth_developing', 'ordered_skills', 'skill_items'],
};

export function canonicalItemType(type) {
  return TYPE_ALIASES[type] || type;
}

export function itemId(item) {
  const id = Number(item?.result_item_id ?? item?.id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function readPattern(source) {
  if (!source || typeof source !== 'object') {
    return null;
  }
  return source.pattern && typeof source.pattern === 'object' ? source.pattern : source;
}

export function readOrderedPatterns(profile = {}) {
  if (profile.rank_1_pattern || profile.rank_2_pattern || profile.rank_3_pattern) {
    return {
      first: readPattern(profile.rank_1_pattern),
      second: readPattern(profile.rank_2_pattern),
      third: readPattern(profile.rank_3_pattern),
    };
  }
  if (profile.first_pattern || profile.second_pattern || profile.third_pattern) {
    return {
      first: readPattern(profile.first_pattern),
      second: readPattern(profile.second_pattern),
      third: readPattern(profile.third_pattern),
    };
  }
  const ranked = [...(profile.ordered_patterns || profile.top_three_patterns || profile.patterns || [])]
    .map(readPattern)
    .filter(Boolean)
    .sort((left, right) => Number(left.rank || left.position || 0) - Number(right.rank || right.position || 0));
  return {
    first: ranked[0] || null,
    second: ranked[1] || null,
    third: ranked[2] || null,
  };
}

export function patternCode(pattern) {
  if (!pattern) {
    return '';
  }
  return String(pattern.short_code || pattern.code || pattern.slug || pattern.key || '').trim();
}

export function canonicalKey(profile = {}) {
  const explicit = profile.canonical_key || profile.top_three_key || profile.profile_key || profile.key;
  if (explicit) {
    return String(explicit);
  }
  const ordered = readOrderedPatterns(profile);
  return [ordered.first, ordered.second, ordered.third]
    .map(patternCode)
    .filter(Boolean)
    .join('-');
}

export function normalizeCoverage(data = {}) {
  const payload = data && typeof data === 'object' ? data : {};
  const nested = payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
    ? payload.data
    : payload;
  const missingByType = nested.missing_content_by_type && typeof nested.missing_content_by_type === 'object'
    ? nested.missing_content_by_type
    : {};
  const expected = Number(
    nested.expected_profiles
    ?? nested.expected_top_three_profiles
    ?? nested.expected
    ?? nested.total_expected
    ?? EXPECTED_TOP_THREE_PROFILE_COUNT,
  );
  const foundRaw = nested.actual_profiles
    ?? nested.found_profiles
    ?? nested.generated_top_three_profiles
    ?? nested.found
    ?? nested.profiles_count;
  const found = Number(foundRaw);
  const complete = Number(
    nested.complete_profiles
    ?? nested.complete
    ?? nested.complete_top_three_profiles
    ?? nested.complete_count
    ?? nested.completed
    ?? 0,
  );
  const incomplete = Number(
    nested.incomplete_profiles
    ?? nested.incomplete
    ?? nested.incomplete_top_three_profiles
    ?? nested.incomplete_count
    ?? Math.max(0, found - complete),
  );
  const isComplete = nested.is_complete === true
    || (Number.isFinite(complete) && Number.isFinite(found) && Number.isFinite(expected)
      && complete === found && found === expected && incomplete === 0);
  return {
    expectedProfiles: Number.isFinite(expected) ? expected : EXPECTED_TOP_THREE_PROFILE_COUNT,
    foundProfiles: Number.isFinite(found) ? found : 0,
    complete: Number.isFinite(complete) ? complete : 0,
    incomplete: Number.isFinite(incomplete) ? incomplete : 0,
    isComplete,
    missingPersonalSummaries: Number(
      nested.missing_personal_summaries
      ?? nested.missing_personal_summary
      ?? missingByType.personal_summary
      ?? nested.personal_summaries_missing
      ?? 0,
    ),
    missingStrengths: Number(
      nested.missing_strengths
      ?? nested.missing_strength
      ?? missingByType.key_strengths
      ?? nested.key_strengths_missing
      ?? 0,
    ),
    missingGrowth: Number(
      nested.missing_growth
      ?? nested.missing_growth_opportunity
      ?? missingByType.growth_opportunities
      ?? nested.growth_content_missing
      ?? nested.growth_opportunities_missing
      ?? 0,
    ),
    missingCareerPaths: Number(
      nested.missing_career_paths
      ?? missingByType.career_paths
      ?? nested.career_paths_missing
      ?? 0,
    ),
    missingSkills: Number(
      nested.missing_skills
      ?? missingByType.skills
      ?? nested.skills_missing
      ?? 0,
    ),
    warnings: Array.isArray(nested.warnings) ? nested.warnings : [],
  };
}

export function isPublishedProfile(profile) {
  const status = profile?.version_status || profile?.version?.status || profile?.status;
  if (profile?.content_editable === true || profile?.is_readonly === false) {
    return false;
  }
  return status === 'archived'
    || profile?.is_readonly === true
    || profile?.is_read_only === true;
}

function readItems(source, keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

export function itemsByRelation(profile = {}) {
  const mixed = profile.ordered_items || profile.result_items || [];
  const grouped = {
    key_strength: [],
    professional_growth_challenge_opportunity: [],
    suggested_career_path: [],
    skill_worth_developing: [],
  };
  RESULT_ITEM_TYPES.forEach((type) => {
    const typed = readItems(profile, RELATION_KEYS[type]);
    if (typed.length > 0) {
      grouped[type] = typed;
      return;
    }
    grouped[type] = mixed.filter((item) => canonicalItemType(item?.canonical_type || item?.type) === type);
  });
  return grouped;
}

export function relationCounts(profile = {}) {
  const grouped = itemsByRelation(profile);
  return {
    key_strength: Number(profile.key_strength_count ?? grouped.key_strength.length),
    professional_growth_challenge_opportunity: Number(
      profile.growth_count
      ?? profile.professional_growth_challenge_opportunity_count
      ?? grouped.professional_growth_challenge_opportunity.length,
    ),
    suggested_career_path: Number(profile.career_path_count ?? grouped.suggested_career_path.length),
    skill_worth_developing: Number(profile.skill_count ?? grouped.skill_worth_developing.length),
  };
}

export function hasPersonalSummary(profile = {}) {
  return Boolean(
    profile.has_personal_summary
    ?? profile.personal_summary
    ?? (Number(profile.personal_summaries_count) > 0),
  );
}

export function missingContentKeys(profile = {}) {
  const provided = profile.missing || profile.missing_content || profile.missing_required_content;
  if (Array.isArray(provided) && provided.length > 0) {
    return provided.map((item) => {
      if (typeof item === 'string') {
        return canonicalItemType(item) === item && item !== 'personal_summary' ? item : (item === 'personal_summary' ? item : canonicalItemType(item));
      }
      return canonicalItemType(item?.type || item?.code || item?.key) || String(item);
    });
  }
  const missing = [];
  if (!hasPersonalSummary(profile)) {
    missing.push('personal_summary');
  }
  const counts = relationCounts(profile);
  RELATION_TYPES.forEach((type) => {
    if (Number(counts[type]) <= 0) {
      missing.push(type);
    }
  });
  return missing;
}

export function isProfileComplete(profile = {}) {
  if (typeof profile.is_complete === 'boolean') {
    return profile.is_complete;
  }
  if (typeof profile.complete === 'boolean') {
    return profile.complete;
  }
  return missingContentKeys(profile).length === 0;
}

export function completenessLabelKey(profile) {
  return isProfileComplete(profile) ? 'profiles.complete' : 'profiles.incomplete';
}

export function patternLabel(pattern, locale) {
  return localizedName(pattern, locale) || patternCode(pattern) || '—';
}

function orderedRelationPayload(items = []) {
  const seen = new Set();
  const payload = [];
  (items || []).forEach((item) => {
    const id = itemId(item);
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    payload.push({
      result_item_id: id,
      sort_order: payload.length + 1,
    });
  });
  return payload;
}

export function buildProfileUpdatePayload({
  personalSummaryId,
  keyStrengths = [],
  growth = [],
  careerPaths = [],
  skills = [],
  contentRevision,
} = {}) {
  const summary = personalSummaryId == null || personalSummaryId === ''
    ? null
    : Number(personalSummaryId);
  const payload = {
    personal_summary_id: Number.isFinite(summary) && summary > 0 ? summary : null,
    key_strengths: orderedRelationPayload(keyStrengths),
    professional_growth_challenge_opportunities: orderedRelationPayload(growth),
    suggested_career_paths: orderedRelationPayload(careerPaths),
    skills_worth_developing: orderedRelationPayload(skills),
  };
  const revision = Number(contentRevision);
  if (Number.isFinite(revision) && revision > 0) {
    payload.content_revision = revision;
  }
  return payload;
}

export function isBuiltVersion(version) {
  if (!version || version.status === 'archived') {
    return false;
  }
  if (typeof version.is_built === 'boolean') {
    return version.is_built;
  }
  return Boolean(version.configuration_checksum && version.scoring_algorithm)
    || version.status === 'published'
    || version.status === 'built';
}

export function selectBuiltVersion(versions = [], requestedId = '') {
  const list = versions || [];
  const built = list.filter(isBuiltVersion);
  const selectable = list.filter((version) => isBuiltVersion(version) || version?.status === 'archived');
  if (requestedId) {
    const byId = selectable.find((version) => String(version.id) === String(requestedId));
    if (byId) {
      return byId;
    }
    const byNumber = selectable.find((version) => String(version.number) === String(requestedId));
    if (byNumber) {
      return byNumber;
    }
  }
  return [...built].sort((left, right) => {
    const numberDelta = Number(right.number || 0) - Number(left.number || 0);
    if (numberDelta !== 0) {
      return numberDelta;
    }
    return Number(right.id || 0) - Number(left.id || 0);
  })[0] || null;
}

export function draftFromProfile(profile = {}) {
  const grouped = itemsByRelation(profile);
  return {
    personalSummary: profile.personal_summary || null,
    keyStrengths: grouped.key_strength,
    growth: grouped.professional_growth_challenge_opportunity,
    careerPaths: grouped.suggested_career_path,
    skills: grouped.skill_worth_developing,
  };
}

export function sameItemSet(left = [], right = []) {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((item, index) => itemId(item) === itemId(right[index]));
}

export function isDraftDirty(draft, original) {
  if (!draft || !original) {
    return false;
  }
  const leftSummary = itemId(draft.personalSummary) || Number(draft.personalSummary?.id) || null;
  const rightSummary = itemId(original.personalSummary) || Number(original.personalSummary?.id) || null;
  return Number(leftSummary || 0) !== Number(rightSummary || 0)
    || !sameItemSet(draft.keyStrengths, original.keyStrengths)
    || !sameItemSet(draft.growth, original.growth)
    || !sameItemSet(draft.careerPaths, original.careerPaths)
    || !sameItemSet(draft.skills, original.skills);
}

export function attachedIds(items = []) {
  return new Set((items || []).map(itemId).filter(Boolean).map(String));
}

export function withoutDuplicate(items, nextItem) {
  const id = itemId(nextItem);
  if (!id) {
    return items;
  }
  if ((items || []).some((item) => itemId(item) === id)) {
    return items;
  }
  return [...(items || []), nextItem];
}

export function uniqueRelationIds(ids = []) {
  return uniqueIds(ids);
}

export function readAttemptTopThree(result = {}) {
  const ordered = readOrderedPatterns(result);
  return {
    profileId: result.top_three_profile_id ?? result.profile_id ?? result.resolved_profile_id ?? null,
    canonicalKey: canonicalKey(result),
    first: ordered.first,
    second: ordered.second,
    third: ordered.third,
    tieBreakingApplied: Boolean(
      result.tie_breaking_applied
      ?? result.tie_break_applied
      ?? result.used_tie_breaker
      ?? result.is_top_tied,
    ),
    snapshotRevision: result.content_snapshot_revision ?? result.snapshot_revision ?? result.content_revision ?? null,
    patterns: result.patterns || result.scores || [],
  };
}

export function rankingFromPatterns(patterns = []) {
  return [...(patterns || [])].sort((left, right) => {
    const rankDelta = Number(left.rank || 0) - Number(right.rank || 0);
    if (rankDelta !== 0) {
      return rankDelta;
    }
    return Number(right.percentage ?? right.score ?? 0) - Number(left.percentage ?? left.score ?? 0);
  });
}
